import { useCallback, useEffect, useState } from "react";
import type { ApiError } from "../api/client";
import { toApiError } from "./errors";

interface LoaderState<T> {
  version: number;
  data: T | undefined;
  error: ApiError | undefined;
  loadedAt: number;
}

export interface Loader<T> {
  data: T | undefined;
  error: ApiError | undefined;
  loading: boolean;
  /** When data last arrived (ms since epoch) — lets render code compare against "now" without calling Date.now(). */
  loadedAt: number;
  reload: () => void;
  replace: (data: T) => void;
}

/** Runs `load` on mount and whenever it changes identity; wrap it in useCallback. */
export function useLoader<T>(load: () => Promise<T>): Loader<T> {
  const [version, setVersion] = useState(0);
  const [state, setState] = useState<LoaderState<T>>({
    version: -1,
    data: undefined,
    error: undefined,
    loadedAt: 0,
  });

  useEffect(() => {
    let active = true;
    load().then(
      (data) => {
        if (active) {
          setState({ version, data, error: undefined, loadedAt: Date.now() });
        }
      },
      (error: unknown) => {
        if (active) {
          setState((previous) => ({ ...previous, version, error: toApiError(error) }));
        }
      },
    );
    return () => {
      active = false;
    };
  }, [load, version]);

  const reload = useCallback(() => setVersion((current) => current + 1), []);
  const replace = useCallback(
    (data: T) => setState((previous) => ({ ...previous, data, error: undefined, loadedAt: Date.now() })),
    [],
  );

  return {
    data: state.data,
    error: state.error,
    loading: state.version !== version,
    loadedAt: state.loadedAt,
    reload,
    replace,
  };
}
